import { Component, Input, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChecklistAnswer } from 'src/app/classes/checklist-answer';
import { ChecklistCompleted } from 'src/app/classes/checklist-completed';
import { ChecklistPointType } from 'src/app/classes/checklist-point';
import { ChecklistQuestion, QuestionType } from 'src/app/classes/checklist-question';
import { CodaltComponent } from 'src/app/codalt.component';
import { ChecklistSignOffService } from 'src/app/services/checklist-sign-off.service';
import { ChecklistService } from 'src/app/services/checklist.service';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';

interface QuestionGroup {
    header: ChecklistQuestion | null;
    questions: ChecklistQuestion[];
    betweenHeaders: boolean;
}

@Component({
    selector: 'app-checklist-pdf',
    templateUrl: './checklist-pdf.component.html',
    styleUrls: ['./checklist-pdf.component.scss'],
    standalone: false,
})
export class ChecklistPdfComponent extends CodaltComponent implements OnInit {
    @Input()
    completed!: ChecklistCompleted;
    @Input()
    interactive = false;
    questionAnswerMap = new Map<number, ChecklistAnswer | null>();
    questions = new Map<number, ChecklistQuestion>();
    questionGroups = signal<QuestionGroup[]>([]);
    ChecklistPointType = ChecklistPointType;
    Settings = Settings;

    constructor(
        private route: ActivatedRoute,
        private checklistService: ChecklistService,
        private checklistSignOffService: ChecklistSignOffService
    ) {
        super();
    }

    ngOnInit(): void {
        if (!this.completed) {
            this.subscriptions.add(
                this.route.params.subscribe(params => {
                    if (params['token']) {
                        LocalStorage.setUserToken(params['token']);
                    }
                    this.getData(params['completedId']);
                })
            );
        } else {
            this.processData();
        }
    }

    private getData(id: number | string): void {
        const completedId = Number(id);
        if (Number.isNaN(completedId)) {
            return;
        }

        this.checklistService.getAnswers(completedId).subscribe(completed => {
            this.completed = completed.data;
            this.processData();
        });
    }

    private processData() {
        this.questionAnswerMap.clear();
        this.questions.clear();
        this.questionGroups.set([]);

        const checklistQuestions = this.completed.checklist_type.questions ?? [];

        checklistQuestions.forEach(question => {
            const answer = this.completed.answers.find(a => a.question_id === question.id) ?? null;
            this.questionAnswerMap.set(question.id, answer);
        });

        checklistQuestions.forEach(question => {
            this.questions.set(question.id, question);
        });

        this.questionGroups.set(this.buildQuestionGroups(checklistQuestions));

        setTimeout(() => {
            document.body.appendChild(document.createElement('readyforpuppeteer'));
        }, 1000);
    }

    private buildQuestionGroups(checklistQuestions: ChecklistQuestion[]): QuestionGroup[] {
        const groups: QuestionGroup[] = [];
        let currentGroup: QuestionGroup = {
            header: null,
            questions: [],
            betweenHeaders: false,
        };

        checklistQuestions.forEach(question => {
            if (this.isHeaderQuestionType(question.question_type)) {
                if (currentGroup.header || currentGroup.questions.length > 0) {
                    groups.push(currentGroup);
                }

                currentGroup = {
                    header: question,
                    questions: [],
                    betweenHeaders: false,
                };

                return;
            }

            currentGroup.questions.push(question);
        });

        if (currentGroup.header || currentGroup.questions.length > 0) {
            groups.push(currentGroup);
        }

        groups.forEach((group, index) => {
            const nextGroup = groups[index + 1];
            group.betweenHeaders = !!group.header && !!nextGroup?.header;
        });

        return groups;
    }

    private isHeaderQuestionType(questionType?: QuestionType): boolean {
        return questionType === QuestionType.YES_NO_HEADER || questionType === QuestionType.HEADER;
    }

    onSignOff(answer: ChecklistAnswer) {
        this.checklistService.signOff(answer.id).subscribe(response => {
            answer.signed_off_at = response.data.signed_off_at;
            this.checklistSignOffService.refresh();
        });
    }
}
